import { ChangeEvent, FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Switch, Form, Input, Select } from 'antd';
import { useRouteMatch } from 'react-router-dom';
import { useStore } from 'effector-react';
import useTranslate from '@hooks/useTranslate';
import useSpamFilters from '@hooks/useSpamFilters';
import componentContent from './SpamFilters.content.json';
import IPost from '@interfaces/IPost';
import localeWeightStore, { localeWeightMap, setLocaleWeight, LOCALE } from '@stores/localeWeight';
import routes, { RouteItem } from '@config/routes';

const { spam, spamComments } = routes;
const spamRoutes = [spam, spamComments];
const { Option } = Select;

function getDisplayCheckedContent(content: Record<string, string>, route?: RouteItem) {
  if (!route) {
    return content.displayChecked;
  }

  switch (route.name) {
    case 'spam':
      return content.displayCheckedPosts;
    case 'spamComments':
      return content.displayCheckedComments;
    default:
      return content.displayChecked;
  }
}

interface SpamFiltersProps {}

const SpamFilters: FunctionComponent<SpamFiltersProps> = () => {
  const [id, setId] = useState<IPost['id'] | undefined>(undefined);
  const [nickname, setNickname] = useState('');

  const { spam, getById, getList, setFilters } = useSpamFilters();
  const { filters, preloaders } = useStore(spam);

  const content = useTranslate(componentContent);

  const match = useRouteMatch();
  const currentSpamPage = spamRoutes.find((route) => route.path === match.path);
  const contentDisplayChecked = getDisplayCheckedContent(content, currentSpamPage);

  const localeName = useStore(localeWeightStore);

  useEffect(() => {
    setId(filters.id ?? '');
    setNickname(filters.author ?? '');
  }, [filters]);

  const isSearchByBlockedFilter = useMemo(() => {
    const { id, nickname } = filters;
    return Boolean(id || nickname);
  }, [filters]);

  const onIdChange = (e: ChangeEvent<HTMLInputElement>) => {
    setId(e.currentTarget.value);
  };

  const onNicknameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNickname(e.currentTarget.value);
  };

  const displayChecked = (value: boolean) => {
    getList({ filters: { ...filters, needChecked: value } });
  };

  const searchById = (postId: string) => {
    const { id, author, ...otherFilters } = filters;

    if (postId) {
      getById(postId);
      setFilters({ id: postId, ...otherFilters });
    } else {
      getList({ filters: { ...otherFilters } });
    }
  };

  const searchByNickname = (author: string) => {
    const { id, ...otherFilters } = filters;
    getList({ filters: { ...otherFilters, author: author || undefined } });
  };

  const onChangeLocale = (newLocale: LOCALE) => {
    setLocaleWeight(newLocale);
    getList();
  };

  return (
    <Form layout='vertical'>
      <Form.Item label={content.byNicknameLabel}>
        <Input.Search
          allowClear
          value={nickname}
          onSearch={searchByNickname}
          onChange={onNicknameChange}
        />
      </Form.Item>

      <Form.Item label={content.byIdLabel}>
        <Input.Search
          allowClear
          value={id}
          onSearch={searchById}
          onChange={onIdChange}
        />
      </Form.Item>

      <Form.Item label={contentDisplayChecked}>
        <Switch
          checked={filters.needChecked}
          disabled={isSearchByBlockedFilter}
          onChange={displayChecked}
          loading={!!preloaders.getList}
        />
      </Form.Item>

      <Form.Item label={content.locale}>
        <Select
          value={localeName}
          disabled={isSearchByBlockedFilter}
          style={{ width: 120 }}
          onChange={onChangeLocale}
        >
          {localeWeightMap &&
            Object.entries(localeWeightMap).map(([key, value]) => {
              return (
                <Option
                  value={key}
                  key={key}
                >
                  {value.name}
                </Option>
              );
            })}
        </Select>
      </Form.Item>
    </Form>
  );
};

export default SpamFilters;
